import { useState, useEffect, useRef } from "react";
import type { DebugLogEntry } from "@/types";
import { humanizeDebugMessage } from "@/lib/error-display";

const LEVEL_STYLES: Record<string, string> = {
  info: "text-cyan-400",
  warn: "text-gold",
  error: "text-brand-red",
  debug: "text-stone-500",
};

function formatTime(timestamp: number): string {
  const d = new Date(timestamp);
  return d.toLocaleTimeString("en-AU", {
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

function formatTimeFull(timestamp: number): string {
  return new Date(timestamp).toISOString();
}

function DetailModal({
  entry,
  onClose,
}: {
  entry: DebugLogEntry;
  onClose: () => void;
}) {
  const dataStr = entry.data != null
    ? JSON.stringify(entry.data, null, 2)
    : null;

  return (
    <div
      data-private="debug-detail"
      className="ph-no-capture fixed inset-0 z-50 flex items-center justify-center bg-navy-dark/80 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-navy border border-navy-light rounded-xl shadow-2xl w-[600px] max-w-[90vw] max-h-[80vh] flex flex-col animate-scale-in">
        <div className="flex items-center justify-between px-4 py-3 border-b border-navy-light">
          <div className="flex items-center gap-2 min-w-0">
            <span
              className={`font-mono text-xs font-semibold ${LEVEL_STYLES[entry.level] || "text-stone-500"}`}
            >
              {entry.level.toUpperCase()}
            </span>
            <span className="text-stone-500 font-mono text-xs">
              [{entry.source}]
            </span>
            <span className="text-stone-400 font-mono text-[10px]">
              {formatTimeFull(entry.timestamp)}
            </span>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded text-stone-500 hover:text-stone-300 hover:bg-navy-light transition-colors flex-shrink-0"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18 18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          <div>
            <div className="font-mono text-[10px] font-medium text-stone-500 uppercase tracking-wider mb-1">
              Message
            </div>
            <div className="font-mono text-xs text-stone-200 bg-navy-dark rounded-lg p-3 whitespace-pre-wrap break-all">
              {entry.message}
            </div>
          </div>
          {dataStr && (
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="font-mono text-[10px] font-medium text-stone-500 uppercase tracking-wider">
                  Data
                </span>
                <button
                  onClick={() => window.freshvibe.copyToClipboard(dataStr)}
                  className="px-2 py-0.5 rounded font-mono text-[10px] text-stone-500 hover:text-gold hover:bg-navy-light transition-colors"
                >
                  Copy
                </button>
              </div>
              <pre className="font-mono text-[11px] text-stone-300 bg-navy-dark rounded-lg p-3 overflow-x-auto max-h-[40vh] whitespace-pre">
                {dataStr}
              </pre>
            </div>
          )}
          {!dataStr && (
            <div className="font-mono text-xs text-stone-600 italic">
              No additional data attached to this entry.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Verbose toggle (small pill switch) ─────────────────────────────────

function VerboseToggle({
  verbose,
  onChange,
}: {
  verbose: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <button
      onClick={() => onChange(!verbose)}
      className="flex items-center gap-1.5 group"
      title={verbose ? "Showing all levels" : "Hiding debug-level entries"}
    >
      <span className="font-mono text-[10px] text-stone-500 group-hover:text-stone-400 transition-colors">
        verbose
      </span>
      <span
        className={`relative inline-flex h-3 w-[22px] items-center rounded-full transition-colors ${
          verbose ? "bg-cyan-500/60" : "bg-stone-700"
        }`}
      >
        <span
          className={`inline-block h-2 w-2 rounded-full bg-white transition-transform ${
            verbose ? "translate-x-[11px]" : "translate-x-[3px]"
          }`}
        />
      </span>
    </button>
  );
}

// ─── Shared debug log content (used by BottomPanel and DebugPanel) ──────

export function DebugLogContent({
  entries,
  onClear,
}: {
  entries: DebugLogEntry[];
  onClear: () => void;
}) {
  const bottomRef = useRef<HTMLDivElement>(null);
  const [selectedEntry, setSelectedEntry] = useState<DebugLogEntry | null>(
    null,
  );
  const [verbose, setVerbose] = useState(false);

  const filtered = verbose
    ? entries
    : entries.filter((e) => e.level !== "debug");

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [filtered]);

  return (
    <>
      <div className="flex-1 flex flex-col min-h-0">
        <div className="flex items-center justify-end gap-2 px-3 py-1 border-b border-navy-light/50">
          <VerboseToggle verbose={verbose} onChange={setVerbose} />
          <button
            onClick={onClear}
            className="px-2 py-0.5 rounded font-mono text-[10px] text-stone-500 hover:text-stone-300 hover:bg-navy-light transition-colors"
          >
            Clear
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-3 py-1 font-mono text-[11px] leading-relaxed">
          {filtered.length === 0 && (
            <div className="text-stone-600 py-4 text-center">
              {entries.length > 0 ? "No entries at this level" : "No debug entries yet"}
            </div>
          )}
          {filtered.map((entry, index) => (
            <div
              key={`${entry.id}:${index}`}
              onClick={() => setSelectedEntry(entry)}
              className="flex gap-2 py-0.5 hover:bg-navy/50 cursor-pointer rounded px-1 -mx-1"
            >
              <span className="text-stone-600 flex-shrink-0">
                {formatTime(entry.timestamp)}
              </span>
              <span
                className={`flex-shrink-0 w-12 text-right ${LEVEL_STYLES[entry.level] || "text-stone-500"}`}
              >
                {entry.level}
              </span>
              <span className="text-stone-500 flex-shrink-0 w-10">
                [{entry.source}]
              </span>
              <span className="text-stone-300 truncate flex-1">
                {humanizeDebugMessage(entry.message)}
              </span>
              {entry.data != null && (
                <span className="text-stone-600 flex-shrink-0">{"{}"}</span>
              )}
            </div>
          ))}
          <div ref={bottomRef} />
        </div>
      </div>

      {selectedEntry && (
        <DetailModal
          entry={selectedEntry}
          onClose={() => setSelectedEntry(null)}
        />
      )}
    </>
  );
}

// ─── Standalone debug panel (design system specimen) ────────────────────

interface Props {
  entries: DebugLogEntry[];
  visible: boolean;
  onClear: () => void;
  onClose: () => void;
}

export function DebugPanel({ entries, visible, onClear, onClose }: Props) {
  if (!visible) return null;

  return (
    <div className="border-t border-navy-light bg-navy-dark flex flex-col h-48">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-1.5 border-b border-navy-light">
        <div className="flex items-center gap-2">
          <svg
            className="w-3.5 h-3.5 text-stone-400"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M6.75 7.5l3 2.25-3 2.25m4.5 0h3m-9 8.25h13.5A2.25 2.25 0 0021 18V6a2.25 2.25 0 00-2.25-2.25H5.25A2.25 2.25 0 003 6v12a2.25 2.25 0 002.25 2.25z"
            />
          </svg>
          <span className="font-mono text-[10px] font-medium text-stone-400 uppercase tracking-wider">
            Debug Log
          </span>
          <span className="font-mono text-[10px] text-stone-500">
            ({entries.length})
          </span>
        </div>
        <button
          onClick={onClose}
          className="p-1 rounded text-stone-500 hover:text-stone-300 hover:bg-navy-light transition-colors"
        >
          <svg
            className="w-3 h-3"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M6 18 18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      {/* Shared content */}
      <DebugLogContent entries={entries} onClear={onClear} />
    </div>
  );
}
